import type { AppApiClient } from '../auth/AppApiClient';
import { gameAssetResources, type GameAssetResources } from '../assets/GameAssetResources';
import type { AssetLease } from '../assets/AssetContracts';
import {
  validateLoadingScreenSnapshot,
  type LoadingScreenBackground,
  type LoadingScreenSlot,
  type LoadingScreenSnapshot,
} from './LoadingScreenContracts';

const STORAGE_KEY = 'rocket-soccer.loading-screens.v1';
const RESOURCE_PREFIX = '/__loading_screen_assets__/';

export interface LoadingScreenBackgroundTarget {
  setBackground(url: string | null, focalPoint?: Readonly<{ x: number; y: number }>): void;
}

/**
 * Owns one loading-screen display. Cached metadata may paint first, while the public
 * settings endpoint is revalidated immediately and can replace the background in-place.
 */
export class LoadingScreenBackgroundSession {
  readonly #abortController = new AbortController();
  #lease: AssetLease | null = null;
  #desiredSelectionKey: string | null = null;
  #disposed = false;

  constructor(
    private readonly slot: LoadingScreenSlot,
    private readonly api: Pick<AppApiClient, 'getLoadingScreens' | 'resolveLoadingScreenRendition'>,
    private readonly target: LoadingScreenBackgroundTarget,
    private readonly resources: GameAssetResources = gameAssetResources,
  ) {}

  start(): void {
    const cached = readCachedSnapshot();
    if (cached) this.#select(cached[this.slot]);
    void this.#refresh();
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#abortController.abort();
    this.#lease?.release();
    this.#lease = null;
  }

  async #refresh(): Promise<void> {
    try {
      const snapshot = await this.api.getLoadingScreens(this.#abortController.signal);
      validateLoadingScreenSnapshot(snapshot);
      if (this.#disposed) return;
      writeCachedSnapshot(snapshot);
      this.#select(snapshot[this.slot]);
    } catch (error) {
      if (!this.#abortController.signal.aborted) console.warn('Loading screen settings could not be refreshed.', error);
    }
  }

  #select(background: LoadingScreenBackground | null): void {
    const selectionKey = background
      ? `${background.revisionId}:${background.focalPoint.x}:${background.focalPoint.y}`
      : null;
    if (selectionKey === this.#desiredSelectionKey) return;
    this.#desiredSelectionKey = selectionKey;
    if (!background) {
      this.#lease?.release();
      this.#lease = null;
      this.target.setBackground(null);
      return;
    }
    void this.#acquire(background, `${background.revisionId}:${background.focalPoint.x}:${background.focalPoint.y}`);
  }

  async #acquire(background: LoadingScreenBackground, desiredSelectionKey: string): Promise<void> {
    const rendition = selectRendition(background);
    const url = `${RESOURCE_PREFIX}${encodeURIComponent(rendition.descriptor.id)}`;
    this.resources.register(url, rendition.descriptor, {
      resolve: async (_descriptor, signal) => this.api.resolveLoadingScreenRendition(rendition.descriptor.id, signal),
    });
    let nextLease: AssetLease | null = null;
    try {
      nextLease = await this.resources.acquire(url, { signal: this.#abortController.signal, priority: 10 });
      const objectUrl = nextLease.objectUrl();
      await decodeImage(objectUrl, this.#abortController.signal);
      if (this.#disposed || this.#desiredSelectionKey !== desiredSelectionKey) {
        nextLease.release();
        return;
      }
      const previous = this.#lease;
      this.#lease = nextLease;
      nextLease = null;
      this.target.setBackground(objectUrl, background.focalPoint);
      previous?.release();
    } catch (error) {
      nextLease?.release();
      if (!this.#abortController.signal.aborted && this.#desiredSelectionKey === desiredSelectionKey) {
        console.warn('Loading screen background could not be acquired.', error);
      }
    }
  }
}

function selectRendition(background: LoadingScreenBackground) {
  const targetWidth = Math.ceil(Math.max(1, globalThis.innerWidth || 1920) * Math.min(globalThis.devicePixelRatio || 1, 2));
  return background.renditions.find(candidate => candidate.width >= targetWidth) ?? background.renditions.at(-1)!;
}

function readCachedSnapshot(): LoadingScreenSnapshot | null {
  try {
    const encoded = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!encoded) return null;
    const value: unknown = JSON.parse(encoded);
    validateLoadingScreenSnapshot(value);
    return value;
  } catch {
    try { globalThis.localStorage?.removeItem(STORAGE_KEY); } catch { /* Storage is optional. */ }
    return null;
  }
}

function writeCachedSnapshot(snapshot: LoadingScreenSnapshot): void {
  try { globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(snapshot)); }
  catch { /* Loading remains functional without persistent metadata. */ }
}

async function decodeImage(url: string, signal: AbortSignal): Promise<void> {
  signal.throwIfAborted();
  if (typeof Image === 'undefined') return;
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  await new Promise<void>((resolve, reject) => {
    const abort = () => reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', abort, { once: true });
    image.decode().then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}
